import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../common/store";
import { axiosClient } from "../../../common/api/axios-client";
import { resetAppState, setHeartbeat, setSessionId } from "../../../common/store/app-state/app-state-slice";
import { v4 } from "uuid";
import { resetChatState,setReqNudgeType } from "../../../common/store/chat/chat-slice";
import { createHeartbeatWorker } from "./heartbeat.worker";
import { sendMessageLogSubmission } from "../../../common/store/chat/thunks/send-message-log";
import VurbalizeEvents from "../../../common/types/dto/chat/vurbalize-events";
import getMessageByVurablizeEvent from "../../utils/get-message-by-vurablize-event";

import { appStateSelector } from "../../../common/store/app-state/selectors";
import { RequestNudgeType } from "../../utils/request-nudge-type";
const getCheckConsent = async (merchId: string, su_id: string, conv_id: string) => {
  try {
    const response = await axiosClient.post<{
      consent_flag: boolean;
      should_reset_session_id: boolean;
    }>('/checkconsent', {
      cust_id: merchId,
      su_id: su_id,
      conversation_id: conv_id
    });
    return response.data;
  } catch (error) {
    return {
      consent_flag: false,
      should_reset_session_id: false
    };
  }
}

export const HEARTBEAT_INTERVAL = 2000;
export const CONSENT_FLAG_KEY = "consent_flag";
export const TAB_LIST_KEY = "active_tabs";
export const useHeartBeat = () => {
  const storeAppState = useAppSelector(appStateSelector);
  const heartbeatThreshold = storeAppState.heartBeatThreshold
  const merchId = useAppSelector(state => state['app-state'].merchId);
  const suId = useAppSelector(state => state['app-state'].suId);
  const convId = useAppSelector(state => state['app-state'].conversationId);
  const dispatch = useAppDispatch();
  const workerRef = useRef<Worker | null>(null);
  const shouldResetSessionIdRef = useRef(false);
  useEffect(() => {
    const worker = createHeartbeatWorker(heartbeatThreshold);

    workerRef.current = worker;
    const tabId = v4();
    let sentCount = 0

    const sendUserIsActive = () => {
      sentCount++;
      // sending user is active every 3 heartbeats
      if (sentCount % 3 !== 0) return;
      sentCount = 0;
      dispatch(sendMessageLogSubmission({
        data: {
          message: getMessageByVurablizeEvent(VurbalizeEvents.USER_IS_ACTIVE),
          event_name: VurbalizeEvents.USER_IS_ACTIVE,
          event_data: {
            timestamp: Date.now(),
            pageUrl: window.location.href,
          }
        }
      }))
    }

    // Handle storage operations requested by worker
    const handleStorageOperation = (operation: string, key: string, value?: string) => {
      switch (operation) {
        case 'get':
          return localStorage.getItem(key);
        case 'set':
          if (value) localStorage.setItem(key, value);
          break;
        case 'remove':
          localStorage.removeItem(key);
          break;
      }
    };

    // Sync initial state to worker
    const syncInitialState = () => {
      const initialTabs = JSON.parse(localStorage.getItem('active_tabs') || '{}');
      worker.postMessage({
        type: 'INIT',
        payload: { tabId, initialTabs }
      });
    };

    const initialize = async () => {
      const res = await getCheckConsent(merchId, suId, convId);
      const isConsentFirst = res?.consent_flag ?? false;
      const shouldResetSessionID = res?.should_reset_session_id ?? false;

      shouldResetSessionIdRef.current = shouldResetSessionID;

      if (isConsentFirst) {
        worker.postMessage({ type: 'CHECK_CONSENT_ACTIVE_TABS' });
      } else {
        worker.postMessage({ type: 'CHECK_ACTIVE_TABS' });
      }

      if (shouldResetSessionID) {
        worker.postMessage({ type: 'CONSENT_ACTIVE_TABS_RESULT'})
        dispatch(setReqNudgeType(RequestNudgeType.SESSION_START))
      }

      localStorage.removeItem('consent_flag');
    };

    worker.onmessage = (e) => {
      const { type, payload } = e.data;

      switch (type) {
        case 'STORAGE_OPERATION':
          handleStorageOperation(payload.operation, payload.key, payload.value);
          break;

        case 'ACTIVE_TABS_RESULT':
          dispatch(setHeartbeat(true));
          if (payload.noActiveTabs && !checkConsent()) {
            localStorage.removeItem('consent_flag');
            dispatch(resetAppState());
            dispatch(resetChatState());
          }
          break;

        case 'CONSENT_ACTIVE_TABS_RESULT':
          dispatch(setHeartbeat(true));
          // TODO: Uncomment this when we have a way to reset the session ID
          if (shouldResetSessionIdRef.current && payload.noActiveTabs && !checkConsent()) {
            dispatch(setSessionId(v4()));
          }
          // add a variable nudge_type and update that based on session id reset and new page navigation to decide which type of nudge to request from backend.
          break;
      }

      // sendUserIsActive();
    };

    // Handle storage events from other tabs
    const onStorageEvent = (event: StorageEvent) => {
      if (event.key === 'active_tabs') {
        worker.postMessage({
          type: 'SYNC_STORAGE',
          payload: { key: event.key, value: event.newValue }
        });
      }
    };

    window.addEventListener('storage', onStorageEvent);
    syncInitialState();
    initialize();

    return () => {
      worker.postMessage({ type: 'CLEANUP' });
      worker.terminate();
      window.removeEventListener('storage', onStorageEvent);
    };
  }, [dispatch, merchId]);

  // Helper function to check consent (needed for storage event)
  const checkConsent = (): boolean => {
    const consentFlag = localStorage.getItem(CONSENT_FLAG_KEY) === "true";
    const cookies = document.cookie
      .split("; ")
      .reduce<Record<string, string>>((acc, cookie) => {
        const [key, value] = cookie.split("=");
        acc[key] = decodeURIComponent(value);
        return acc;
      }, {});

    const consentCookie = cookies["CONSENTMGR"];
    if (!consentCookie) return false;

    const hasZeroC3 = /c3:0/.test(consentCookie);
    if (hasZeroC3) return false;

    const hasConsent = /consent:true/.test(consentCookie);
    return hasConsent || !!consentFlag;
  };
};